import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import InternalCryptoTransactionPartyControl from './components/InternalCryptoTransactionPartyControl';
import { fromErrorDto, toDto } from './components/CryptoTransactionModel';
import CommissionsList from '../PaymentTransaction/components/CommissionsList';
import {
  AMOUNT_INPUT_VALUE_TYPE,
  CRYPTO_CURRENCY_PRECISION,
  MAX_TRANSACTION_COMMENT_LENGTH,
  PAYMENT_METHOD,
  TRANSACTION_TYPES,
  ERROR_CODES,
  PAYMENT_PROVIDERS,
  TRANSFER_TYPE
} from 'components/constants';
import { useApplicationContext } from 'contexts/ApplicationContext';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { getAllCryptoCurrencies } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import { calculateTransactionCommission } from 'services/requestAgent';
import AmountInput from 'uikit/AmountInput';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './CryptoTransaction.scss';

const CryptoTransaction = ({ transactionsStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const { constants } = useApplicationContext();
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const form = useFormik({
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      transactionType: TRANSACTION_TYPES.INCOMING,
      paymentProvider: PAYMENT_PROVIDERS.GRAPHEREX,
      paymentMethod: PAYMENT_METHOD.CRYPTO,
      senderAccount: null,
      recipientAccount: null,
      senderWalletAddress: '',
      recipientWalletAddress: '',
      amount: '',
      currency: '',
      commissions: [
        {
          tariffId: null,
          amount: '',
          currency: 'EUR',
          label: 'Custom Commission',
          isCustom: true
        }
      ],
      commentToAdmin: ''
    },
    validationSchema: Yup.object({
      paymentProvider: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      paymentMethod: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      senderAccount: Yup.object().when(['transactionType', 'paymentMethod'], {
        is: (transactionType, paymentMethod) =>
          transactionType === TRANSACTION_TYPES.OUTGOING ||
          (transactionType === TRANSACTION_TYPES.INCOMING && paymentMethod === PAYMENT_METHOD.INTERNAL),
        then: () => Yup.object().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.object().nullable().notRequired()
      }),
      recipientAccount: Yup.object().when(['transactionType', 'paymentMethod'], {
        is: (transactionType, paymentMethod) =>
          transactionType === TRANSACTION_TYPES.INCOMING ||
          (transactionType === TRANSACTION_TYPES.OUTGOING && paymentMethod === PAYMENT_METHOD.INTERNAL),
        then: () => Yup.object().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.object().nullable().notRequired()
      }),
      senderWalletAddress: Yup.string().when(['transactionType', 'paymentMethod'], {
        is: (transactionType, paymentMethod) =>
          transactionType === TRANSACTION_TYPES.INCOMING && paymentMethod === PAYMENT_METHOD.CRYPTO,
        then: () =>
          Yup.string()
            .required(i18n.getMessage('createTransaction.validation.isRequired'))
            .max(50, i18n.getMessage('createTransaction.validation.maxSize', { size: 50 })),
        otherwise: () =>
          Yup.string()
            .nullable()
            .notRequired()
            .max(50, i18n.getMessage('createTransaction.validation.maxSize', { size: 50 }))
      }),
      recipientWalletAddress: Yup.string().when(['transactionType', 'paymentMethod'], {
        is: (transactionType, paymentMethod) =>
          transactionType === TRANSACTION_TYPES.OUTGOING && paymentMethod === PAYMENT_METHOD.CRYPTO,
        then: () =>
          Yup.string()
            .required(i18n.getMessage('createTransaction.validation.isRequired'))
            .max(50, i18n.getMessage('createTransaction.validation.maxSize', { size: 50 })),
        otherwise: () =>
          Yup.string()
            .nullable()
            .notRequired()
            .max(50, i18n.getMessage('createTransaction.validation.maxSize', { size: 50 }))
      }),
      amount: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      commissions: Yup.array().of(
        Yup.object().shape({
          tariffId: Yup.number().nullable().positive(i18n.getMessage('createTransaction.validation.mustBePositive')),
          amount: Yup.string()
            .nullable()
            .test('is-positive', i18n.getMessage('createTransaction.validation.mustBePositive'), function (value) {
              if (!value || value === '') return true; // Allow empty values
              const num = parseFloat(value);
              return !isNaN(num) && num > 0;
            })
        })
      ),
      currency: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      commentToAdmin: Yup.string()
        .nullable()
        .max(
          MAX_TRANSACTION_COMMENT_LENGTH,
          i18n.getMessage('createTransaction.validation.maxSize', { size: MAX_TRANSACTION_COMMENT_LENGTH })
        )
    }),
    onSubmit: async (values) => {
      values.transactionType === TRANSACTION_TYPES.OUTGOING
        ? await transactionsStore.createOutgoingTransaction(toDto(values))
        : await transactionsStore.createIncomingTransaction(toDto(values));
    }
  });

  const { values, errors, handleSubmit, handleChange, setErrors, setFieldValue, resetForm, validateField } = form;

  useEffect(() => {
    if (transactionsStore.isNewTransactionCreated) {
      resetForm();
      transactionsStore.setIsNewTransactionCreated(false);
      navigate(`${ROUTE_PATHS.TRANSACTIONS}/${transactionsStore.newTransactionId}`);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsStore.isNewTransactionCreated]);

  const parseServerErrors = (code, details) => {
    if (code === ERROR_CODES.ARGUMENT_NOT_VALID && details) {
      // Convert array of error details to object with field names as keys
      const fieldsObject = {};
      if (Array.isArray(details)) {
        details.forEach((detail) => {
          if (detail.field) {
            fieldsObject[detail.field] = detail.code;
          }
        });
      } else {
        // For backward compatibility, if details is already an object
        Object.assign(fieldsObject, details);
      }

      const converted = fromErrorDto(fieldsObject);
      Object.keys(converted).forEach((key) => {
        converted[key] = converted[key] ? i18n.getMessage(`error.field.${converted[key]}`) : undefined;
      });

      setErrors(converted);
    }
  };

  useEffect(() => {
    if (transactionsStore.error) {
      parseServerErrors(
        transactionsStore.error.code,
        transactionsStore.error.details || transactionsStore.error.fields
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsStore.error]);

  const transactionsTypesOptions = Object.keys(TRANSACTION_TYPES).map((type) => {
    return {
      key: TRANSACTION_TYPES[type],
      value: i18n.getMessage('transactionType.' + TRANSACTION_TYPES[type])
    };
  });

  const getCurrenciesOptions = () => {
    if (constants) {
      const allCryptoCurrencies = getAllCryptoCurrencies(constants);

      return allCryptoCurrencies.map((currency) => {
        return {
          key: currency,
          value: currency
        };
      });
    } else {
      return [];
    }
  };

  useEffect(() => {
    if (constants?.providerProperties?.GRAPHEREX) {
      setCurrencyOptions(getCurrenciesOptions());
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constants?.providerProperties?.GRAPHEREX]);

  useEffect(() => {
    if (values.recipientAccount) {
      validateField('recipientAccount');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.recipientAccount]);

  // Keep commissions currency in sync with the transaction currency
  useEffect(() => {
    if (values.currency && values.commissions?.some((c) => c.currency !== values.currency)) {
      setFieldValue(
        'commissions',
        values.commissions.map((c) => ({ ...c, currency: values.currency }))
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.currency, values.commissions]);

  useEffect(() => {
    if (values.senderAccount) {
      validateField('senderAccount');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.senderAccount]);

  // Calculate commission when payment method or account changes
  useEffect(() => {
    const asyncOperations = async () => {
      let account =
        values.transactionType === TRANSACTION_TYPES.INCOMING ? values.recipientAccount : values.senderAccount;

      // Only calculate if we have all required fields INCLUDING amount
      // But don't trigger on amount change - that's handled by onBlur
      if (values.amount && values.paymentMethod && account) {
        let data = {
          walletNumber: account.wallet_number,
          paymentMethod: values.paymentMethod,
          transactionType: values.transactionType,
          transferType: TRANSFER_TYPE.NORMAL,
          amount: values.amount
        };

        try {
          let response = await calculateTransactionCommission(data);

          // Keep existing custom commissions
          const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];

          // Transform backend response to include tariffId
          if (response.appliedTariffs && response.appliedTariffs.length > 0) {
            const tariffCommissions = response.appliedTariffs.map((tariff) => ({
              tariffId: tariff.tariffId,
              tariffName: tariff.systemTariffKey || `Tariff ${tariff.tariffId}`,
              amount: tariff.amount,
              currency: values.currency || 'EUR',
              feeType: tariff.feeType,
              systemTariffKey: tariff.systemTariffKey,
              highRiskCountries: tariff.highRiskCountries
            }));

            // Combine tariff-based commissions with custom ones
            await setFieldValue('commissions', [...tariffCommissions, ...existingCustomCommissions]);
          } else {
            // No tariffs from backend, keep custom commissions
            if (existingCustomCommissions.length === 0) {
              // Add default custom commission if nothing exists
              await setFieldValue('commissions', [
                {
                  tariffId: null,
                  amount: '',
                  currency: values.currency || 'EUR',
                  label: 'Custom Commission',
                  isCustom: true
                }
              ]);
            }
          }
        } catch (error) {
          // Error calculating commission - keep existing custom commissions
          const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];
          if (existingCustomCommissions.length === 0) {
            await setFieldValue('commissions', [
              {
                tariffId: null,
                amount: '',
                currency: values.currency || 'EUR',
                label: 'Custom Commission',
                isCustom: true
              }
            ]);
          }
        }
      }
    };

    asyncOperations();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.paymentMethod, values.senderAccount, values.recipientAccount]);

  const handleChangeValue = (name, value) => {
    return setFieldValue(name, value);
  };

  const onBlurValidate = (fieldName) => {
    validateField(fieldName);
  };

  const handleAmountBlur = async () => {
    // Remove trailing dot if present
    let cleanedAmount = values.amount;
    if (cleanedAmount && cleanedAmount.endsWith('.')) {
      cleanedAmount = cleanedAmount.slice(0, -1);
      await setFieldValue('amount', cleanedAmount);
    }

    // Validate the field
    validateField('amount');

    // Calculate commission on blur
    let account =
      values.transactionType === TRANSACTION_TYPES.INCOMING ? values.recipientAccount : values.senderAccount;

    if (cleanedAmount && values.paymentMethod && account) {
      let data = {
        walletNumber: account.wallet_number,
        paymentMethod: values.paymentMethod,
        transactionType: values.transactionType,
        transferType: TRANSFER_TYPE.NORMAL,
        amount: cleanedAmount
      };

      try {
        let response = await calculateTransactionCommission(data);

        // Keep existing custom commissions
        const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];

        // Transform backend response to include tariffId
        if (response.appliedTariffs && response.appliedTariffs.length > 0) {
          const tariffCommissions = response.appliedTariffs.map((tariff) => ({
            tariffId: tariff.tariffId,
            tariffName: tariff.systemTariffKey || `Tariff ${tariff.tariffId}`,
            amount: tariff.amount,
            currency: values.currency || 'EUR',
            feeType: tariff.feeType,
            systemTariffKey: tariff.systemTariffKey,
            highRiskCountries: tariff.highRiskCountries
          }));

          // Combine tariff-based commissions with custom ones
          await setFieldValue('commissions', [...tariffCommissions, ...existingCustomCommissions]);
        } else {
          // No tariffs from backend, keep custom commissions
          if (existingCustomCommissions.length === 0) {
            // Add default custom commission if nothing exists
            await setFieldValue('commissions', [
              {
                tariffId: null,
                amount: '',
                currency: values.currency || 'EUR',
                label: 'Custom Commission',
                isCustom: true
              }
            ]);
          }
        }
      } catch (error) {
        // Error calculating commission - keep existing custom commissions
        const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];
        if (existingCustomCommissions.length === 0) {
          await setFieldValue('commissions', [
            {
              tariffId: null,
              amount: '',
              currency: values.currency || 'EUR',
              label: 'Custom Commission',
              isCustom: true
            }
          ]);
        }
      }
    }
  };

  const handleChangeTransactionType = (name, transactionType) => {
    if (transactionType !== values.transactionType) {
      resetForm();
      setFieldValue(name, transactionType);
    }
  };

  return (
    <div className={'crypto-transaction-wrapper'}>
      <div className={'crypto-transaction-type-wrapper'}>
        <InputDropDown
          className={'crypto-transaction-select-transaction-type'}
          label={i18n.getMessage('createTransaction.crypto.transactionType')}
          id={'transactionType'}
          name={'transactionType'}
          value={values.transactionType}
          options={transactionsTypesOptions}
          onChange={handleChangeTransactionType}
        />
      </div>
      <form className={'crypto-transaction-form'} onSubmit={handleSubmit}>
        {values.transactionType === TRANSACTION_TYPES.INCOMING && (
          <>
            <InternalCryptoTransactionPartyControl
              account={values.recipientAccount}
              paymentProvider={values.paymentProvider}
              paymentMethod={values.paymentMethod}
              setCurrencyOptions={setCurrencyOptions}
              handleChangeByFormik={handleChange}
              handleChangeValue={handleChangeValue}
              property={'recipientAccount'}
              values={values}
              accountLabel={i18n.getMessage('createTransaction.crypto.toAccount')}
              isShowPaymentMethod={true}
              isShowPaymentProvider={true}
              errors={errors}
              validateField={onBlurValidate}
            />

            <InternalCryptoTransactionPartyControl
              account={values.senderAccount}
              paymentProvider={values.paymentProvider}
              paymentMethod={values.paymentMethod}
              handleChangeByFormik={handleChange}
              handleChangeValue={handleChangeValue}
              property={values.paymentMethod === PAYMENT_METHOD.CRYPTO ? 'senderWalletAddress' : 'senderAccount'}
              values={values}
              accountLabel={i18n.getMessage('createTransaction.crypto.fromAccount')}
              isShowPaymentMethod={false}
              isShowRegularAccountField={values.paymentMethod === PAYMENT_METHOD.CRYPTO}
              errors={errors}
              validateField={onBlurValidate}
            />
          </>
        )}

        {values.transactionType === TRANSACTION_TYPES.OUTGOING && (
          <>
            <InternalCryptoTransactionPartyControl
              account={values.senderAccount}
              paymentProvider={values.paymentProvider}
              paymentMethod={values.paymentMethod}
              handleChangeByFormik={handleChange}
              handleChangeValue={handleChangeValue}
              property={'senderAccount'}
              values={values}
              accountLabel={i18n.getMessage('createTransaction.crypto.fromAccount')}
              isShowPaymentProvider={true}
              isShowPaymentMethod={true}
              validateField={onBlurValidate}
            />

            <InternalCryptoTransactionPartyControl
              account={values.recipientAccount}
              paymentProvider={values.paymentProvider}
              paymentMethod={values.paymentMethod}
              handleChangeByFormik={handleChange}
              handleChangeValue={handleChangeValue}
              property={values.paymentMethod === PAYMENT_METHOD.CRYPTO ? 'recipientWalletAddress' : 'recipientAccount'}
              values={values}
              accountLabel={i18n.getMessage('createTransaction.crypto.toAccount')}
              isShowPaymentMethod={false}
              isShowRegularAccountField={values.paymentMethod === PAYMENT_METHOD.CRYPTO}
              validateField={onBlurValidate}
            />
          </>
        )}

        <div className={'crypto-transaction-block-wrapper'}>
          <div className={'crypto-transaction-row-block'}>
            <AmountInput
              isRequired
              className={'crypto-transaction-input'}
              label={i18n.getMessage('createTransaction.crypto.amount.label')}
              placeholder={i18n.getMessage('createTransaction.crypto.amount.placeholder')}
              name={'amount'}
              value={values?.amount}
              returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
              precision={CRYPTO_CURRENCY_PRECISION}
              onChange={handleChange}
              onBlur={handleAmountBlur}
              isDisabled={!!values.transitTransaction}
              error={errors?.amount}
            />
            <InputDropDownSearch
              isRequired
              className={'crypto-transaction-select-currency'}
              label={i18n.getMessage('createTransaction.crypto.currency')}
              id={'currency'}
              name={'currency'}
              value={values?.currency}
              options={currencyOptions}
              onChange={handleChangeValue}
              onBlur={() => onBlurValidate('currency')}
              isDisabled={
                (!!values.recipientAccount && values.transactionType === TRANSACTION_TYPES.INCOMING) ||
                values.transactionType === TRANSACTION_TYPES.OUTGOING
              }
              error={errors?.currency}
            />
          </div>

          <CommissionsList
            commissions={values?.commissions || []}
            currency={values.currency}
            onChange={(index, value) => {
              const newCommissions = [...values.commissions];
              newCommissions[index] = { ...newCommissions[index], amount: value };
              setFieldValue('commissions', newCommissions);
            }}
            onBlur={(index) => onBlurValidate(`commissions[${index}].amount`)}
            onDelete={(index) => {
              const newCommissions = [...values.commissions];
              newCommissions.splice(index, 1);
              setFieldValue('commissions', newCommissions);
            }}
            onAdd={() => {
              const newCommissions = [...(values.commissions || [])];
              newCommissions.push({
                tariffId: null,
                amount: '',
                currency: values.currency || 'EUR',
                label: 'Custom Commission',
                isCustom: true
              });
              setFieldValue('commissions', newCommissions);
            }}
            errors={errors?.commissions}
          />

          <Input
            type={'textarea'}
            rows={2}
            className={'crypto-transaction-textarea'}
            label={i18n.getMessage('createTransaction.crypto.commentToAdmin')}
            id={'commentToAdmin'}
            name={'commentToAdmin'}
            value={values.commentToAdmin}
            onChange={handleChange}
            onBlur={() => onBlurValidate('commentToAdmin')}
            error={errors?.commentToAdmin}
            max={MAX_TRANSACTION_COMMENT_LENGTH}
            subText={i18n.getMessage('createTransaction.crypto.symbolsLeft', {
              symbolsLeft: MAX_TRANSACTION_COMMENT_LENGTH - values.commentToAdmin.length
            })}
          />
        </div>

        <div className={'crypto-transaction-submit-button-wrapper'}>
          <Button
            className={'crypto-transaction-submit-button'}
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            onClick={() => {}}
            isDisabled={transactionsStore.isLoading}
          >
            {transactionsStore.isLoading ? (
              <Loader />
            ) : (
              i18n.getMessage('createTransaction.crypto.button.createCryptoTransaction')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

CryptoTransaction.propTypes = {
  transactionsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  transactionsStore: stores.transactionsStore
}))(observer(CryptoTransaction));
